'use client';

import { useEffect, useState } from 'react';
import api from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import {
	Star,
	User,
	MessageSquare,
	Loader2,
	Send,
	AlertCircle,
	CheckCircle2,
} from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

// Type matching the populated backend response
type Review = {
	_id: string;
	user: { _id: string; name: string; isAlumni?: boolean } | string; // Handle populated user object
	rating: number;
	comment: string;
	createdAt: string;
};

export default function Reviews({
	productId,
}: {
	productId: string;
}) {
	const { user } = useAuth();
	const pathname = usePathname();

	// State
	const [reviews, setReviews] = useState<Review[]>([]);
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);

	// Form State
	const [rating, setRating] = useState(0);
	const [comment, setComment] = useState('');
	const [error, setError] = useState<string | null>(null);

	// --- Fetch Reviews ---
	useEffect(() => {
		let mounted = true;
		async function load() {
			try {
				const resp = await api.get(
					`/products/${productId}/reviews`,
				);
				if (mounted) {
					// Backend returns array directly based on controller logic
					setReviews(
						Array.isArray(resp.data)
							? resp.data
							: resp.data.reviews || [],
					);
				}
			} catch (err) {
				console.warn('Failed to load reviews', err);
			} finally {
				if (mounted) setLoading(false);
			}
		}
		load();
		return () => {
			mounted = false;
		};
	}, [productId]);

	// --- Submit Handler ---
	async function submitReview(e: React.FormEvent) {
		e.preventDefault();
		setError(null);

		if (!user) return;
		if (rating === 0) {
			setError('Please select a star rating');
			return;
		}
		if (!comment.trim()) {
			setError('Please write a comment');
			return;
		}

		setSubmitting(true);
		try {
			await api.post(`/products/${productId}/reviews`, {
				rating,
				comment,
			});

			// Optimistically update or re-fetch. Here we re-fetch to be safe.
			const resp = await api.get(
				`/products/${productId}/reviews`,
			);
			setReviews(
				Array.isArray(resp.data)
					? resp.data
					: resp.data.reviews || [],
			);

			// Reset form
			setComment('');
			setRating(0);
		} catch (err: any) {
			setError(
				err?.response?.data?.error ||
					'Failed to submit review',
			);
		} finally {
			setSubmitting(false);
		}
	}


	// --- Submit Reply ---
	const [replyingTo, setReplyingTo] = useState<string | null>(null);
	const [replyText, setReplyText] = useState('');
	const [replyBusy, setReplyBusy] = useState(false);

	async function submitReply(reviewId: string) {
		if (!replyText.trim()) return;
		setReplyBusy(true);
		try {
			await api.post(`/products/${reviewId}/reply`, { text: replyText });

			// Refresh
			const resp = await api.get(`/products/${productId}/reviews`);
			setReviews(Array.isArray(resp.data) ? resp.data : resp.data.reviews || []);

			setReplyingTo(null);
			setReplyText('');
		} catch (err) {
			console.error(err);
		} finally {
			setReplyBusy(false);
		}
	}

	// --- Helpers ---
	const averageRating = reviews.length
		? (
				reviews.reduce((acc, r) => acc + r.rating, 0) /
				reviews.length
		  ).toFixed(1)
		: '0.0';

	return (
		<div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 shadow-sm mt-8">
			{/* --- Header & Summary --- */}
			<div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 border-b border-gray-100 pb-6">
				<div>
					<h2 className="text-2xl font-bold text-[#051449] flex items-center gap-2">
						Customer Reviews
					</h2>
					<p className="text-gray-500 text-sm mt-1">
						What other students are saying
					</p>
				</div>

				<div className="flex items-center gap-4 bg-gray-50 px-5 py-3 rounded-xl">
					<div className="text-3xl font-extrabold text-[#051449]">
						{averageRating}
					</div>
					<div className="flex flex-col">
						<div className="flex text-yellow-400 text-sm">
							{[1, 2, 3, 4, 5].map((star) => (
								<Star
									key={star}
									className={`w-4 h-4 ${
										star <=
										Math.round(Number(averageRating))
											? 'fill-current'
											: 'text-gray-300'
									}`}
								/>
							))}
						</div>
						<span className="text-xs text-gray-500 font-medium mt-0.5">
							Based on {reviews.length} reviews
						</span>
					</div>
				</div>
			</div>

			<div className="grid lg:grid-cols-12 gap-10">
				{/* --- Review List (Left Col) --- */}
				<div className="lg:col-span-7 space-y-6">
					{loading ? (
						<div className="flex flex-col items-center justify-center py-10 text-gray-400">
							<Loader2 className="w-8 h-8 animate-spin mb-2" />
							<p className="text-sm">Loading reviews...</p>
						</div>
					) : reviews.length === 0 ? (
						<div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
							<MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-3" />
							<h3 className="text-gray-900 font-medium">
								No reviews yet
							</h3>
							<p className="text-sm text-gray-500 mt-1">
								Be the first to share your thoughts!
							</p>
						</div>
					) : (
						<div className="space-y-6">
									{reviews.map((r: any) => {
								const userName =
									typeof r.user === 'object'
										? r.user.name
										: 'User';
								return (
									<div
										key={r._id}
										className="border-b border-gray-100 last:border-0 pb-6 last:pb-0"
									>
										<div className="flex items-start justify-between mb-2">
											<div className="flex items-center gap-3">
												<div className="w-10 h-10 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center font-bold text-sm">
													{userName[0].toUpperCase()}
												</div>
												<div>
													<div className="font-semibold text-gray-900 text-sm flex items-center gap-1">
														{userName}
														{typeof r.user === 'object' && r.user.isAlumni && (
															<CheckCircle2 className="w-3 h-3 text-blue-500 fill-blue-100" />
														)}
													</div>
													<div className="flex text-yellow-400 text-xs mt-0.5">
														{[1, 2, 3, 4, 5].map((s) => (
															<Star
																key={s}
																className={`w-3 h-3 ${
																	s <= r.rating
																		? 'fill-current'
																		: 'text-gray-200'
																}`}
															/>
														))}
													</div>
												</div>
											</div>
											<span className="text-xs text-gray-400">
												{new Date(
													r.createdAt,
												).toLocaleDateString(undefined, {
													month: 'short',
													day: 'numeric',
													year: 'numeric',
												})}
											</span>
										</div>
										<p className="text-gray-600 text-sm leading-relaxed pl-[52px]">
											{r.comment}
										</p>

										{/* Admin Reply Display */}
										{r.adminReply && (
											<div className="ml-[52px] mt-4 bg-gray-50 p-4 rounded-xl border-l-4 border-[#FFCF23]">
												<p className="text-xs font-bold text-[#051449] mb-1">
													Admin Response
												</p>
												<p className="text-gray-600 text-sm">
													{r.adminReply.text}
												</p>
											</div>
										)}

										{/* Admin Reply Action */}
										{user?.role === 'admin' && !r.adminReply && (
											<div className="ml-[52px] mt-2">
												{replyingTo === r._id ? (
													<div className="flex gap-2">
														<input
															type="text"
															value={replyText}
															onChange={(e) => setReplyText(e.target.value)}
															placeholder="Write a reply..."
															className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-[#051449]"
															autoFocus
														/>
														<button
															onClick={() => submitReply(r._id)}
															disabled={replyBusy || !replyText.trim()}
															className="px-3 py-2 bg-[#051449] text-white text-xs font-bold rounded-lg disabled:opacity-50"
														>
															{replyBusy ? '...' : 'Send'}
														</button>
														<button
															onClick={() => setReplyingTo(null)}
															className="px-3 py-2 text-gray-500 text-xs hover:bg-gray-100 rounded-lg"
														>
															Cancel
														</button>
													</div>
												) : (
													<button
														onClick={() => {
															setReplyingTo(r._id);
															setReplyText('');
														}}
														className="text-xs font-bold text-blue-600 hover:text-blue-700"
													>
														Reply as Admin
													</button>
												)}
											</div>
										)}
									</div>
								);
							})}
						</div>
					)}
				</div>

				{/* --- Write Review Form (Right Col) --- */}
				{/* Hidden for ADMINS */}
				{user?.role !== 'admin' && (
				<div className="lg:col-span-5">
					<div className="bg-gray-50 rounded-xl p-6 sticky top-24">
						<h3 className="text-lg font-bold text-gray-900 mb-4">
							Write a Review
						</h3>

						{!user ? (
							<div className="text-center py-6">
								<User className="w-8 h-8 text-gray-400 mx-auto mb-2" />
								<p className="text-sm text-gray-600 mb-4">
									You must be logged in to post a review.
								</p>
								<Link
									href={`/auth/login?redirectTo=${encodeURIComponent(
										pathname
									)}`}
									className="inline-block px-6 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
								>
									Log In
								</Link>
							</div>
							) : user.isDisabled ? (
								<div className="text-center py-6 bg-red-50 rounded-xl border border-red-100">
									<AlertCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
									<p className="text-sm text-red-700 font-bold">
										Your account is disabled.
									</p>
									<p className="text-xs text-red-600 mt-1">
										You cannot post reviews at this time.
									</p>
								</div>
						) : (
							<form
								onSubmit={submitReview}
								className="space-y-4"
							>
								<div>
									<label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
										Rating
									</label>
									<div className="flex gap-2">
										{[1, 2, 3, 4, 5].map((star) => (
											<button
												key={star}
												type="button"
												onClick={() => setRating(star)}
												className="focus:outline-none transition-transform hover:scale-110 active:scale-95"
											>
												<Star
													className={`w-8 h-8 ${
														star <= rating
															? 'text-yellow-400 fill-current'
															: 'text-gray-300 hover:text-yellow-200'
													}`}
												/>
											</button>
										))}
									</div>
								</div>

								<div>
									<label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
										Your Comment
									</label>
									<textarea
										value={comment}
										onChange={(e) =>
											setComment(e.target.value)
										}
										rows={4}
										className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#051449]/20 focus:border-[#051449] outline-none text-sm resize-none bg-white"
										placeholder="What did you like or dislike about this product?"
									/>
								</div>

								{error && (
									<div className="p-3 bg-red-50 text-red-600 text-xs rounded-lg flex items-start gap-2">
										<AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
										{error}
									</div>
								)}

								<button
									type="submit"
									disabled={submitting}
									className="w-full py-3 bg-[#051449] text-white rounded-lg font-bold shadow-sm hover:bg-blue-900 transition-all disabled:opacity-70 flex items-center justify-center gap-2"
								>
									{submitting ? (
										<>
											<Loader2 className="w-4 h-4 animate-spin" />
											Submitting...
										</>
									) : (
										<>
											<Send className="w-4 h-4" />
											Submit Review
										</>
									)}
								</button>
							</form>
						)}
					</div>
				</div>
				)}
			</div>
		</div>
	);
}
